import { useRef } from "react";
import { useInView } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { GridPattern } from "@/components/ui/grid-pattern";
import { BlurFade } from "@/components/ui/blur-fade";
import { cn } from "@/lib/utils";

/* ── Component ───────────────────────────────────────────────── */

export interface TeamSectionV2Props {
  className?: string;
}

export function TeamSectionV2({ className }: TeamSectionV2Props) {
  return (
    <section
      id="team"
      className={cn("relative bg-cream overflow-hidden", className)}
      data-testid="section-team"
    >
      <GridPattern
        width={24}
        height={24}
        x={-1}
        y={-1}
        className={cn(
          "z-[1] fill-transparent stroke-[hsla(60,3%,8%,0.10)]",
          "[mask-image:linear-gradient(to_bottom_right,rgba(0,0,0,0.6)_0%,rgba(0,0,0,0.25)_40%,transparent_70%)]",
        )}
      />

      <div className="relative z-[2]">
        <div className="container-content section-padding-y">
          <BlurFade inView delay={0.1}>
            <span className="type-eyebrow" style={{ color: "var(--terra)" }}>
              MEET THE TEAM
            </span>
          </BlurFade>

          <BlurFade inView delay={0.15}>
            <div style={{ maxWidth: "var(--max-w-heading)" }}>
              <h2
                className="type-h1"
                style={{ color: "var(--charcoal)", marginTop: "var(--space-gap-sm)" }}
              >
                Boutique team. Bespoke strategies.
              </h2>
            </div>
          </BlurFade>

          <BlurFade inView delay={0.2}>
            <div style={{ maxWidth: "var(--max-w-text)" }}>
              <p
                className="type-body"
                style={{ color: "var(--charcoal)", marginTop: "var(--space-gap-sm)" }}
              >
                We're an in-house team of passionate advertising experts and
                highly intelligent business analysts. We're unbelievably focused
                and we work hard... and we have a hell of a good time while
                we're at it.
              </p>
            </div>
          </BlurFade>

          <BlurFade inView delay={0.25}>
            <Link href="/about-us">
              <span
                className="type-nav inline-flex items-center cursor-pointer group"
                style={{
                  color: "var(--charcoal)",
                  gap: 8,
                  marginTop: "var(--space-gap-md)",
                  borderBottom: "var(--border-light)",
                  paddingBottom: 4,
                }}
              >
                LEARN MORE ABOUT US
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                  style={{ transitionDuration: "var(--t-quick)" }}
                />
              </span>
            </Link>
          </BlurFade>
        </div>

        <DesktopCollage />
        <MobileCollage />
      </div>
    </section>
  );
}

/* ── Desktop collage ─────────────────────────────────────────── */
/*
  Traced from Webflow reference (1570px viewport, ~710px collage height):

  Webflow proportions (% of viewport width):
  A:  x=0,    w=16.5%   — left couple, bleeds left, nearly full height
  B1: x=19.5, w=13%     — team stairs, top half
  B2: x=19.5, w=13%     — whiteboard, bottom half
  blocks: x=30%, centered between B and C
  C:  x=34,   w=18.5%   — center walking image
  card: x=34, w=18.5%   — careers card below C
  D:  x=54.5, w=26%     — hero portrait, nearly full height (largest)
  E:  x=83,   w=17%+    — single tall right image, bleeds right

  Gaps between columns: ~14px
  Images have NO border-radius
*/

function DesktopCollage() {
  return (
    <div
      className="hidden lg:block relative w-full"
      style={{
        height: "clamp(520px, 46vw, 750px)",
        marginBottom: "var(--space-section-bottom)",
      }}
    >
      {/*
        All gaps = 8px, both horizontal and vertical.
        Column layout (left edges use calc with 8px gaps):
          A: 0,                    w: 16%
          B: calc(16% + 8px),      w: 13%
          C: calc(29% + 16px),     w: 18%    (but pushed down for logo blocks)
          D: calc(47% + 24px),     w: 26%
          E: calc(73% + 32px),     w: remaining (bleeds right)
      */}

      {/* A — couple with flowers, left bleed, tall */}
      <WipeImage
        src="/assets/office/office_team_hallway.webp"
        alt="Team members in the hallway"
        pos="center 20%"
        delay={0}
        style={{
          position: "absolute",
          left: 0,
          top: "4%",
          width: "16%",
          height: "86%",
        }}
      />

      {/* B1 — guitar lounge, upper col 2 */}
      <WipeImage
        src="/assets/office/office_casual_lounge.webp"
        alt="Team hanging out in the lounge"
        pos="center 45%"
        delay={0.06}
        style={{
          position: "absolute",
          left: "calc(16% + 8px)",
          top: "18%",
          width: "13%",
          height: "30%",
        }}
      />

      {/* B2 — one on one meeting, 8px below B1 */}
      <WipeImage
        src="/assets/office/office_one_on_one.webp"
        alt="One on one meeting"
        pos="center 30%"
        delay={0.1}
        style={{
          position: "absolute",
          left: "calc(16% + 8px)",
          top: "calc(48% + 8px)",
          width: "13%",
          height: "40%",
        }}
      />

      {/*
        Color accent blocks — L-shape (2x2, bottom-left empty).
        The empty cell IS B1's corner. All gaps = 8px.
        B1 right = calc(29% + 8px), B1 top = 18%.
      */}
      <div
        style={{
          position: "absolute",
          left: "calc(29% + 8px - 48px)",
          top: "calc(18% - 56px)",
          display: "grid",
          gridTemplateColumns: "48px 48px",
          gridTemplateRows: "48px 48px",
          gap: 8,
          zIndex: 3,
        }}
      >
        <div style={{ backgroundColor: "var(--terra)" }} />
        <div style={{ backgroundColor: "var(--indigo)" }} />
        <div />
        <div style={{ backgroundColor: "var(--charcoal)" }} />
      </div>

      {/* C — 3 women collaborating, 8px below charcoal block */}
      <WipeImage
        src="/assets/office/office_team_workstations_1.webp"
        alt="Team collaborating"
        pos="center 25%"
        delay={0.08}
        style={{
          position: "absolute",
          left: "calc(29% + 16px)",
          top: "calc(18% + 56px)",
          width: "18%",
          height: "38%",
        }}
      />

      {/* Careers card — 8px below C */}
      <div
        style={{
          position: "absolute",
          left: "calc(29% + 16px)",
          top: "calc(56% + 64px)",
          width: "18%",
          bottom: "3%",
          backgroundColor: "hsla(60, 3%, 8%, 0.04)",
          border: "var(--border-light-muted)",
          padding: "var(--pad-md)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <h3 className="type-h4" style={{ color: "var(--charcoal)" }}>
          Interested in joining our team?
        </h3>
        <p className="type-body-sm" style={{ color: "var(--charcoal)", marginTop: 4 }}>
          We're always looking for the right person.
        </p>
        <a
          href="/careers"
          className="type-nav inline-flex items-center group"
          style={{
            color: "var(--charcoal)",
            gap: 8,
            marginTop: "var(--space-gap-sm)",
            borderBottom: "var(--border-light)",
            paddingBottom: 4,
            alignSelf: "flex-start",
          }}
        >
          VIEW OPEN POSITIONS
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-1"
            style={{ transitionDuration: "var(--t-quick)" }}
          />
        </a>
      </div>

      {/* D — couch conversation (hero, largest), 8px gap from C column */}
      <WipeImage
        src="/assets/office/office_casual_conversation.webp"
        alt="Patrick on the couch"
        pos="center 30%"
        delay={0.04}
        style={{
          position: "absolute",
          left: "calc(47% + 24px)",
          top: "2%",
          width: "26%",
          height: "88%",
        }}
      />

      {/* E — conference presentation, 8px gap from D, bleeds right */}
      <WipeImage
        src="/assets/office/office_conference_table.webp"
        alt="Team presentation"
        pos="center 40%"
        delay={0.12}
        style={{
          position: "absolute",
          left: "calc(73% + 32px)",
          top: "10%",
          right: 0,
          height: "70%",
        }}
      />
    </div>
  );
}

/* ── Mobile collage ──────────────────────────────────────────── */

function MobileCollage() {
  return (
    <div
      className="lg:hidden grid grid-cols-2"
      style={{
        gap: 14,
        padding: "0 var(--pad-container)",
        marginBottom: "var(--space-section-bottom)",
      }}
    >
      <WipeImage
        src="/assets/office/office_team_hallway.webp"
        alt="Team in the hallway"
        pos="center 20%"
        delay={0}
        style={{ aspectRatio: "3 / 4" }}
      />
      <WipeImage
        src="/assets/office/office_casual_conversation.webp"
        alt="Patrick on the couch"
        pos="center 30%"
        delay={0.06}
        style={{ aspectRatio: "3 / 4" }}
      />
      <WipeImage
        src="/assets/office/office_full_team.webp"
        alt="Full AdVenture team"
        pos="center 35%"
        delay={0.1}
        style={{ aspectRatio: "3 / 4" }}
      />
      <WipeImage
        src="/assets/office/office_conference_table.webp"
        alt="Team presentation"
        pos="center 40%"
        delay={0.14}
        style={{ aspectRatio: "3 / 4" }}
      />
      <div
        className="col-span-2"
        style={{
          backgroundColor: "hsla(60, 3%, 8%, 0.04)",
          padding: "var(--pad-md)",
        }}
      >
        <h3 className="type-h4" style={{ color: "var(--charcoal)" }}>
          Interested in joining our team?
        </h3>
        <p className="type-body-sm" style={{ color: "var(--charcoal)", marginTop: 4 }}>
          We're always looking for the right person.
        </p>
        <a
          href="/careers"
          className="type-nav inline-flex items-center group"
          style={{
            color: "var(--charcoal)",
            gap: 8,
            marginTop: "var(--space-gap-sm)",
            borderBottom: "var(--border-light)",
            paddingBottom: 4,
          }}
        >
          VIEW OPEN POSITIONS
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" style={{ transitionDuration: "var(--t-quick)" }} />
        </a>
      </div>
    </div>
  );
}

/* ── Image with wipe-to-reveal ───────────────────────────────── */

function WipeImage({
  src,
  alt,
  pos,
  delay,
  style,
}: {
  src: string;
  alt: string;
  pos: string;
  delay: number;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  // Plain <img> only — the optimize-images pipeline emits inconsistent variant
  // coverage (some office files have -480 only, some -480 + -800, none have
  // -1200). Building a srcSet that references missing widths makes <picture>
  // pick a 404'd URL based on viewport size and the browser then shows the
  // broken-image placeholder instead of falling back to the base <img>.
  // Using just the base file is the safe behavior until the variants are
  // backfilled. Base files exist for every team image.
  const imgStyle: React.CSSProperties = {
    objectPosition: pos,
    clipPath: isInView ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
    transition: `clip-path 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
  };

  return (
    <div ref={ref} className="overflow-hidden" style={style}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        width={600}
        height={800}
        style={imgStyle}
      />
    </div>
  );
}
